import { Tier } from '@trustpad/launchpad';

import Link from 'next/link';
import * as React from 'react';
import { FaCheck } from 'react-icons/fa';

import { CardTitle, DividerLine, StandardCard } from '~common/UI';
import { Popup } from '~common/components/Popup';
import { tiers } from '~config/tiers';
import { fNum, formatShort } from '~utils';

const MIN_AAG_TIER = tiers['associate'];
const MIN_SEED_TIER = tiers['orbit'];

const TierGroupTitle = ({ children }) => (
  <h4 className="text-white text-lg rounded uppercase inline-block">
    {children}
  </h4>
);

export function TiersListCard({ activeTierId }: { activeTierId: string }) {
  const lotteryLevels = Object.values(tiers).filter(
    (t) => t.minAmount > 0 && t.random,
  );
  const superAllAccessGuaranteedLevels = Object.values(tiers).filter(
    (t) => t.vip,
  );
  const allAccessGuaranteedLevels = Object.values(tiers).filter(
    (t) => t.multiplier >= MIN_AAG_TIER.multiplier && !t.vip,
  );
  const guaranteedLevels = Object.values(tiers).filter(
    (t) =>
      t.minAmount > 0 &&
      !t.random &&
      !t.vip &&
      !allAccessGuaranteedLevels.map((t) => t.id).includes(t.id),
  );

  return (
    <StandardCard cardClassName="bg-opacity-90" className="space-y-3">
      <h3 className="text-gray-100">Levels</h3>

      <p className="text-gray-100">
        To be eligible for any of the tiers you are required to stake the
        following:
      </p>

      <div>
        <div className="mt-4 mb-1">
          <TierGroupTitle>Guaranteed</TierGroupTitle>
          <Popup
            btnClassName="-mt-[0.10em] text-white ml-2"
            header="Guaranteed levels">
            Guaranteed (G) levels provide guaranteed allocations in normal
            sales, IDOs. By default, private, strategic and seed sales are not
            available in this echelon.
          </Popup>
        </div>

        {guaranteedLevels.map((tier, k) => (
          <TierRow
            key={tier.id}
            idx={lotteryLevels.length + k + 1}
            tier={tier}
            activeTierId={activeTierId}
          />
        ))}

        <div className="mt-4 mb-1">
          <TierGroupTitle>All-Access Guaranteed</TierGroupTitle>
          <Popup
            btnClassName="-mt-[0.10em] text-white ml-2"
            header="All-Access Guaranteed levels">
            <p>
              All-Access Guaranteed (AAG) levels provide guaranteed allocations
              also in the strategic, private and seed sales.
            </p>
            <p>
              The AAG echelon also provides higher allocation boosters compared
              to the G echelon.
            </p>
          </Popup>
        </div>

        {allAccessGuaranteedLevels.map((tier, k) => (
          <TierRow
            key={tier.id}
            idx={lotteryLevels.length + guaranteedLevels.length + k + 1}
            tier={tier}
            activeTierId={activeTierId}
          />
        ))}

        <div className="mt-4 mb-1">
          <TierGroupTitle>Super All-Access Guaranteed</TierGroupTitle>
          <Popup
            btnClassName="-mt-[0.10em] text-white ml-2"
            header="All-Access Guaranteed levels">
            <p>
              Super All-Access Guaranteed (SAAG) levels provide guaranteed
              allocations also in the strategic, private and seed sales. They
              also have special extra benefits such as airdrops in incubated
              projects (more to be announced soon).
            </p>
            <p>
              The SAAG echelon also provides higher allocation boosters compared
              to the G and AAG echelons.
            </p>
          </Popup>
        </div>

        {superAllAccessGuaranteedLevels.map((tier, k) => (
          <TierRow
            key={tier.id}
            idx={
              lotteryLevels.length +
              guaranteedLevels.length +
              allAccessGuaranteedLevels.length +
              k +
              1
            }
            tier={tier}
            activeTierId={activeTierId}
          />
        ))}
      </div>
    </StandardCard>
  );
}

function TierRow({
  tier,
  activeTierId,
  idx,
}: {
  tier: Tier;
  activeTierId: string;
  idx: number;
}) {
  return (
    <div className="odd:bg-gray-500/10 even:bg-transparent rounded">
      <div
        className={`py-1 px-1 rounded inline-block text-gray-100 ${
          tier.vip ? 'bg-black' : ''
        }`}
        style={tier.vip ? { boxShadow: '0px 0px 6px 2px black' } : {}}>
        <span className="brand-text font-normal">
          {formatShort(tier.minAmount)} TPAD
        </span>{' '}
        <span className=""></span> for{' '}
        {tier.vip ? (
          <span
            className="font-secondary tracking-wide"
            style={{ textShadow: '0px 0px 7px #ffffff8a' }}>
            {tier.name}
          </span>
        ) : (
          <span className="font-secondary tracking-wide">{tier.name}</span>
        )}{' '}
        [{tier.multiplier}x]
        {tier.random && ` (${tier.odds}% lottery)`}
        {tier.id === activeTierId && (
          <FaCheck className="inline text-green-600 ml-2 -mt-1" />
        )}
      </div>
    </div>
  );
}
